// Hold Orders — K3 (Kalpana UAT round 4 doc DMS-20260428).
// Sits between Pending Orders and Scheduled Splits in the sidebar flow.
// Lists all orders in lifecycle_stage='on_hold' with hold-by, hold-reason,
// last-update. Inline reminder button (reuses orderApi.remind).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { orderApi } from '../api/services';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { Pause, Loader2, Bell } from 'lucide-react';

export default function HoldOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    orderApi.onHold()
      .then((res) => setOrders(res.data.orders || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function ping(id) {
    const note = window.prompt('Optional note (or leave empty):', '');
    if (note === null) return;
    try {
      await orderApi.remind(id, note || null);
      toast.success('Reminder sent to Admin and Ops');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reminder');
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <p className="text-sm text-gray-500 mb-4">
        Orders currently on hold. Use <strong>Send reminder</strong> to ping
        Admin / Ops if a hold has been sitting too long without an update.
      </p>

      {loading ? (
        <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          <Pause className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No orders on hold right now.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Hold by</th>
                <th className="text-left px-3 py-2">Hold reason</th>
                <th className="text-left px-3 py-2">From file</th>
                <th className="text-left px-3 py-2">Last update</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/orders/${o.id}`} className="text-blue-600 hover:underline">
                      {o.order_id_external}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{o.customer_name}</td>
                  <td className="px-3 py-2 text-xs">
                    {o.hold_by
                      ? <span>
                          <span className="capitalize">{o.hold_by.team || 'system'}</span>
                          {(o.hold_by.user_name || o.hold_by.user_email) && (
                            <span className="text-gray-500"> · {o.hold_by.user_name || o.hold_by.user_email}</span>
                          )}
                        </span>
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{o.hold_reason_label || o.current_owner_reason_code || '—'}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-600 max-w-[180px] truncate"
                      title={o.intake_filename || ''}>
                    {o.intake_filename || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {o.current_owner_since
                      ? formatDistanceToNow(new Date(o.current_owner_since), { addSuffix: true })
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => ping(o.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-amber-100 text-amber-900 hover:bg-amber-200">
                      <Bell className="w-3 h-3" /> Send reminder
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
